/**
 * D5 — gestão das categorias de grupo: nome + cor de uma paleta FECHADA (`chart-1…8`).
 *
 * O recorte de cada categoria (grupos, pessoas, vagas) vem contado do servidor; aqui só se
 * escreve a frase. Apagar a categoria não apaga grupo nenhum — os grupos só perdem a etiqueta,
 * e o diálogo diz isso antes do clique.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  FormAlert,
  IconButton,
  Input,
  NativeSelect,
  SkeletonText,
  useToast,
} from '@mgdlead/ui';
import { Plus, Trash2 } from 'lucide-react';

import {
  CATEGORY_COLORS,
  CATEGORY_COLOR_NAME,
  categorySummary,
  type CategoryColor,
} from '../../../features/group/category-ui';
import {
  useCreateGroupCategory,
  useGroupCategories,
  useRemoveGroupCategory,
  useUpdateGroupCategory,
} from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';
import { CategoryBadge } from './CategoryBadge';

export interface CategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  canEdit: boolean;
}

const COLOR_OPTIONS = CATEGORY_COLORS.map((c) => ({ value: c, label: CATEGORY_COLOR_NAME[c] }));

export function CategoriesDialog({
  open,
  onOpenChange,
  workspaceId,
  canEdit,
}: CategoriesDialogProps) {
  const { toast } = useToast();
  const query = useGroupCategories(workspaceId);
  const create = useCreateGroupCategory();
  const update = useUpdateGroupCategory();
  const remove = useRemoveGroupCategory();

  const [nome, setNome] = useState('');
  const [cor, setCor] = useState<CategoryColor>('chart-1');
  const [erro, setErro] = useState<string | null>(null);

  const criar = () => {
    setErro(null);
    create.mutate(
      { workspaceId, name: nome.trim(), color: cor },
      {
        onSuccess: () => {
          setNome('');
          toast({ title: 'Categoria criada', tone: 'success' });
        },
        onError: (e: unknown) => setErro(humanizeError(e)),
      },
    );
  };

  const categorias = query.data ?? [];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Categorias de grupo"
      description="Etiquetas para organizar e filtrar os grupos. Cada uma tem uma cor."
      size="lg"
    >
      <div className="flex flex-col gap-4">
        {query.isPending ? (
          <SkeletonText lines={3} />
        ) : query.isError ? (
          <FormAlert>{humanizeError(query.error)}</FormAlert>
        ) : categorias.length === 0 ? (
          <EmptyState
            kind="zero"
            title="Nenhuma categoria ainda"
            description="Crie a primeira abaixo — por exemplo “Ofertas SP” ou “VIP”."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {categorias.map((categoria) => (
              <li key={categoria.id} className="flex flex-wrap items-center gap-2 py-2.5">
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <CategoryBadge name={categoria.name} color={categoria.color} />
                  <span className="nums text-2xs text-subtle-foreground">
                    {categorySummary(categoria)}
                  </span>
                </div>
                {canEdit ? (
                  <>
                    <NativeSelect
                      size="sm"
                      aria-label={`Cor de ${categoria.name}`}
                      value={categoria.color}
                      options={COLOR_OPTIONS}
                      disabled={update.isPending}
                      onChange={(event) =>
                        update.mutate(
                          {
                            workspaceId,
                            categoryId: categoria.id,
                            color: event.target.value as CategoryColor,
                          },
                          { onError: (e: unknown) => setErro(humanizeError(e)) },
                        )
                      }
                    />
                    <IconButton
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 />}
                      label={`Apagar a categoria ${categoria.name}. Os grupos continuam, só perdem a etiqueta.`}
                      disabled={remove.isPending}
                      onClick={() =>
                        remove.mutate(
                          { workspaceId, categoryId: categoria.id },
                          { onError: (e: unknown) => setErro(humanizeError(e)) },
                        )
                      }
                    />
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {erro !== null ? <FormAlert>{erro}</FormAlert> : null}

        {canEdit ? (
          <form
            className="flex flex-col gap-2 md:flex-row md:items-end"
            onSubmit={(event) => {
              event.preventDefault();
              if (nome.trim().length > 0 && !create.isPending) criar();
            }}
          >
            <Field label="Nova categoria" className="flex-1">
              {(control) => (
                <Input
                  {...control}
                  value={nome}
                  maxLength={40}
                  placeholder="Ex.: Ofertas SP"
                  onChange={(event) => setNome(event.target.value)}
                />
              )}
            </Field>
            <Field label="Cor">
              {(control) => (
                <NativeSelect
                  {...control}
                  value={cor}
                  options={COLOR_OPTIONS}
                  onChange={(event) => setCor(event.target.value as CategoryColor)}
                />
              )}
            </Field>
            <Button
              type="submit"
              variant="neutral"
              leadingIcon={<Plus />}
              loading={create.isPending}
              loadingLabel="Criando"
              disabled={nome.trim().length === 0}
            >
              Criar
            </Button>
          </form>
        ) : null}
      </div>
    </Dialog>
  );
}
